import React, { useCallback, useEffect, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { thaiDate, todayISO } from './nurseOptions';
import ThaiDateInput from '../components/ThaiDateInput'

// ฉีดวัคซีนเป็นรุ่น — สาธารณสุขเข้ามาฉีดทีละห้อง หน้านี้จึงเลือกห้องแล้วติ๊กคนที่ฉีด
// ไม่ใช่กรอกทีละคน. UNIQUE (นักเรียน, วัคซีน, เข็มที่, วันที่) ทำให้กดซ้ำไม่เกิดแถวซ้ำ
export default function NurseVaccine() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearId } = useStudyYearSelector();

  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState('');
  const [names, setNames] = useState([]);
  const [form, setForm] = useState({ vacName: '', dose: '1', date: todayISO(), place: '', note: '' });
  const [students, setStudents] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!yearId) return;
    axiosInstance.get(`/api/nurse/rooms?yearId=${yearId}`)
      .then(res => setRooms(res.data || []))
      .catch(() => setRooms([]));
    axiosInstance.get('/api/nurse/vaccine-names')
      .then(res => setNames(res.data || []))
      .catch(() => setNames([]));
  }, [yearId]);

  const fetchRoom = useCallback(() => {
    if (!roomId) { setStudents([]); return; }
    setLoading(true);
    axiosInstance.get(`/api/nurse/vaccines?roomId=${roomId}&vacName=${encodeURIComponent(form.vacName)}`)
      .then(res => {
        setStudents(res.data || []);
        setPicked(new Set());
      })
      .catch(() => setStudents([]))
      .finally(() => setLoading(false));
  }, [roomId, form.vacName]);

  useEffect(() => { fetchRoom(); }, [fetchRoom]);

  const toggle = (stId) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(stId)) next.delete(stId); else next.add(stId);
    return next;
  });

  // "เลือกคนที่ยังไม่ได้ฉีด" คือปุ่มที่ใช้จริงบ่อยที่สุด — เลือกทั้งห้องแล้วไล่เอาคนที่
  // ฉีดไปแล้วออกคือการทำงานซ้ำ
  const pickPending = () =>
    setPicked(new Set(students.filter(s => !s.nvc_id).map(s => s.st_id)));

  const save = () => {
    if (!form.vacName.trim()) {
      Swal.fire({ icon: 'info', title: 'กรุณาระบุชื่อวัคซีน' });
      return;
    }
    if (!picked.size) {
      Swal.fire({ icon: 'info', title: 'ยังไม่ได้เลือกนักเรียน' });
      return;
    }
    setSaving(true);
    axiosInstance.post('/api/nurse/vaccines', {
      vacName: form.vacName.trim(),
      dose: form.dose,
      date: form.date,
      place: form.place,
      note: form.note,
      studentIds: [...picked],
    }).then(({ data }) => {
      fetchRoom();
      Swal.fire({
        icon: 'success',
        title: 'บันทึกแล้ว',
        text: `บันทึก ${data.inserted} คน${data.skipped ? ` · ข้ามที่บันทึกไว้แล้ว ${data.skipped} คน` : ''}`,
        timer: 2000,
        showConfirmButton: false,
      });
    }).catch(err => Swal.fire({ icon: 'error', title: 'ไม่สามารถบันทึกได้', text: err?.response?.data?.message || '' }))
      .finally(() => setSaving(false));
  };

  const removeVaccine = (row) => {
    Swal.fire({
      icon: 'warning',
      title: 'ลบรายการวัคซีนนี้?',
      html: `<div class="fw-semibold">${row.st_name}</div><div>${form.vacName} เข็มที่ ${row.vac_dose || '-'}</div>`,
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ปิด',
      confirmButtonColor: '#dc3545',
      reverseButtons: true,
    }).then(result => {
      if (!result.isConfirmed) return;
      axiosInstance.delete(`/api/nurse/vaccines/${row.nvc_id}`)
        .then(() => fetchRoom())
        .catch(err => Swal.fire({ icon: 'error', title: 'ลบไม่สำเร็จ', text: err?.response?.data?.message || '' }));
    });
  };

  const doneCount = students.filter(s => s.nvc_id).length;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="mb-3">
          <ol className="breadcrumb fs-sm mb-1">
            <li className="breadcrumb-item">ห้องพยาบาล</li>
            <li className="breadcrumb-item active">วัคซีน</li>
          </ol>
          <h4 className="main-title mb-0">บันทึกการฉีดวัคซีน</h4>
          <div className="fs-xs text-secondary">เลือกห้อง ระบุวัคซีน แล้วติ๊กนักเรียนที่ได้รับ</div>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Row className="g-2 align-items-end">
              <Col xs={12} md={3}>
                <Form.Label className="fw-semibold fs-xs mb-1">ห้องเรียน</Form.Label>
                <Form.Select size="sm" value={roomId} onChange={e => setRoomId(e.target.value)}>
                  <option value="">— เลือกห้อง —</option>
                  {rooms.map(r => (
                    <option key={r.r_id} value={r.r_id}>ม.{r.c_id}/{r.r_no} ({r.student_count} คน)</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={3}>
                <Form.Label className="fw-semibold fs-xs mb-1">ชื่อวัคซีน</Form.Label>
                <Form.Control size="sm" list="vaccine-names" value={form.vacName} maxLength={100}
                  onChange={e => setForm({ ...form, vacName: e.target.value })}
                  placeholder="เช่น ไข้หวัดใหญ่, dT, HPV" />
                <datalist id="vaccine-names">
                  {names.map(n => <option key={n.vac_name} value={n.vac_name} />)}
                </datalist>
              </Col>
              <Col xs={4} md={1}>
                <Form.Label className="fw-semibold fs-xs mb-1">เข็มที่</Form.Label>
                <Form.Control size="sm" value={form.dose} maxLength={20}
                  onChange={e => setForm({ ...form, dose: e.target.value })} />
              </Col>
              <Col xs={8} md={2}>
                <Form.Label className="fw-semibold fs-xs mb-1">วันที่ฉีด</Form.Label>
                <ThaiDateInput size="sm" value={form.date}
                  onChange={v => setForm({ ...form, date: v })} />
              </Col>
              <Col xs={12} md={3}>
                <Form.Label className="fw-semibold fs-xs mb-1">สถานที่ / หน่วยงาน</Form.Label>
                <Form.Control size="sm" value={form.place} maxLength={255}
                  onChange={e => setForm({ ...form, place: e.target.value })}
                  placeholder="เช่น รพ.สต.พ่อมิ่ง" />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Header className="d-flex align-items-center gap-2 flex-wrap">
            <Card.Title as="h6" className="mb-0">
              {students.length ? `นักเรียน ${students.length} คน · บันทึกแล้ว ${doneCount} คน` : 'รายชื่อนักเรียน'}
            </Card.Title>
            {students.length > 0 && (
              <div className="ms-auto d-flex gap-2">
                <Button size="sm" variant="outline-secondary" onClick={pickPending}>
                  เลือกคนที่ยังไม่ได้ฉีด
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => setPicked(new Set())}>
                  ล้างที่เลือก
                </Button>
                <Button size="sm" onClick={save} disabled={saving || !picked.size}>
                  {saving ? <Spinner size="sm" animation="border" />
                    : <><i className="ri-syringe-line me-1"></i>บันทึก {picked.size} คน</>}
                </Button>
              </div>
            )}
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : !students.length ? (
              <div className="text-center text-secondary py-5 fs-sm">เลือกห้องเรียนเพื่อเริ่ม</div>
            ) : (
              <div className="table-responsive">
                <Table hover className="mb-0 fs-sm align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: 50 }}></th>
                      <th>นักเรียน</th>
                      <th>สถานะ</th>
                      <th style={{ width: 60 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map(s => (
                      <tr key={`${s.st_id}-${s.nvc_id || 'new'}`}>
                        <td className="text-center">
                          <Form.Check checked={picked.has(s.st_id)} onChange={() => toggle(s.st_id)}
                            aria-label={`เลือก ${s.st_name}`} />
                        </td>
                        <td>
                          <div className="fw-semibold">{s.st_name}</div>
                          <div className="fs-xs text-secondary">{s.st_idnumber}</div>
                        </td>
                        <td>
                          {s.nvc_id ? (
                            <>
                              <Badge bg="success" className="fw-normal">
                                ฉีดแล้ว {thaiDate(s.vac_date)}
                              </Badge>
                              <div className="fs-xs text-secondary">
                                เข็มที่ {s.vac_dose || '-'}{s.vac_place ? ` · ${s.vac_place}` : ''}
                              </div>
                            </>
                          ) : (
                            <span className="text-secondary fs-xs">
                              {form.vacName ? 'ยังไม่มีบันทึก' : 'ระบุชื่อวัคซีนเพื่อดูสถานะ'}
                            </span>
                          )}
                        </td>
                        <td className="text-end">
                          {s.nvc_id && (
                            <Button size="sm" variant="outline-danger" onClick={() => removeVaccine(s)}
                              aria-label="ลบรายการ">
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
        <Footer />
      </div>
    </>
  );
}
